import { type FC, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ButtonWithDropdown from '__components/Button/WithDropdown';
import { SMARTCONNECTORS_PATHNAME } from 'pages/SmartConnectors/constants';
import { useRunSmartConnectorModal } from 'pages/SmartConnectors/components/RunSmartConnectorModal/useRunSmartConnectorModal';
import { RunSmartConnectorModal } from 'pages/SmartConnectors/components/RunSmartConnectorModal';
import type { SmartConnector } from 'pages/SmartConnectors/types';
import SmartConnectorService from 'services/SmartConnectorService';
import { useToast, toastVariant } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { getOriginalError } from 'services/AxiosService';
import LoadingButton from '__components/Button/LoadingButton';
import useModal from '__components/Modals/useModal';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import { Nav } from 'components/Navigation/NavLayout';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import { SmartConnectorAccess } from 'pages/SmartConnectors/hooks/useSmartConnectorAccess';
import { subroutes } from './navigation';

export const ControlBar: FC<{
  smartConnector: SmartConnector;
  access: SmartConnectorAccess;
  onStartRun: () => void;
}> = ({ smartConnector, access, onStartRun }) => {
  const { t } = useTranslation();
  const params = useParams<{ id: string; subpage: string }>();
  const history = useHistory();
  const [showToast] = useToast();

  const [deleting, setDeleting] = useState(false);

  const { runSmartConnectorModalProps, showRunSmartConnectorModal } =
    useRunSmartConnectorModal(onStartRun);

  const runSmartConnector = (isDryRun: boolean) => {
    showRunSmartConnectorModal({
      smartConnector,
      isDryRun,
      canSkipDiffCheck: smartConnector.is_diff_check_enabled,
    });
  };

  const startRun = () => {
    runSmartConnector(false);
  };

  const startDryRun = () => {
    runSmartConnector(true);
  };

  const handleAction = ({ value }: { value: 'dry_run' }) => {
    if (value === 'dry_run') {
      runSmartConnector(true);
    }
  };

  const actionOptions = useMemo(
    () => [{ value: 'dry_run', label: t('Start Dry Run') }],
    [t]
  );

  const deleteSmartConector = async () => {
    setDeleting(true);
    try {
      await SmartConnectorService.deleteSmartConnector(smartConnector.id!);
      history.replace(SMARTCONNECTORS_PATHNAME);
    } catch (err) {
      showToast({
        message:
          getErrorMessage(getOriginalError(err)) ||
          t('Failted to delete smartConnector'),
        variant: toastVariant.FAILURE,
      });
    } finally {
      setDeleting(false);
    }
  };

  const [deleteModalProps, deleteButtonProps] = useModal({
    handleSubmit: deleteSmartConector,
  });

  return (
    <>
      <Nav.Container>
        <Nav.TopNavRow>
          <Nav.TopNavLeftContainer>
            <Nav.TopNavBackButton
              label={t('back to smartConnectors')}
              path={SMARTCONNECTORS_PATHNAME}
            />
          </Nav.TopNavLeftContainer>
          <Nav.TopNavCenterContainer>
            <Nav.TopNavHeader label={smartConnector.name} />
          </Nav.TopNavCenterContainer>
          <Nav.TopNavRightContainer>
            {access.remove ? (
              <LoadingButton
                color="red"
                {...deleteButtonProps}
                disabled={deleting}
                loading={deleting}
              >
                {t('Delete')}
              </LoadingButton>
            ) : null}
            {access.view ? (
              getIsSmartConnectorActive(smartConnector) ? (
                <ButtonWithDropdown
                  noSpace
                  color="green"
                  onClick={startRun}
                  onChange={handleAction}
                  options={actionOptions}
                >
                  {t('Start Run')}
                </ButtonWithDropdown>
              ) : (
                <LoadingButton color="green" onClick={startDryRun}>
                  {t('Start Dry Run')}
                </LoadingButton>
              )
            ) : null}
          </Nav.TopNavRightContainer>
        </Nav.TopNavRow>
        <Nav.SubNavigationRow>
          {Object.values(subroutes).map((route, i, arr) => {
            return (
              <Nav.SubNavigationLink
                key={route.path}
                route={route}
                isLast={i === arr.length - 1}
                numberOfLinks={arr.length}
                params={params}
              />
            );
          })}
        </Nav.SubNavigationRow>
      </Nav.Container>
      {runSmartConnectorModalProps.show ? (
        <RunSmartConnectorModal {...runSmartConnectorModalProps} />
      ) : null}
      <ConfirmDeletionModal {...deleteModalProps}>
        {t(
          'This will permanently delete the SmartConnector and uploads will no longer be able to be processed through these steps.'
        )}
      </ConfirmDeletionModal>
    </>
  );
};
